import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { CloseCircleFilled } from '@ant-design/icons';
import { Col, Divider, Empty, Form, Row, Steps, Typography } from 'antd';

import gridSelector from 'common/components/grid-view/controllers/selectors';
import {
  CustomNotification,
  StepForm,
  StyledModal,
  ThumbnailItem,
  UploadFile,
} from 'common/components';
import * as actionsGridView from 'common/components/grid-view/controllers/actions';

import CertificateForm from './CertificateForm';
import CertificateFooter from './CertificateFooter';

import { useUpdateCertificate, useValidateCertificate } from './hook';
import { useGetProductFullView } from 'hooks';
import { exportFormDateWithoutTime } from 'utils/formatDate';

import { UPLOAD_DIGITAL_ASSET_FOR_PRODUCT } from 'services/product/endpoints';

import {
  ASSET_TYPE,
  DOCUMENT_SUBTYPE,
  UPLOAD_MAX_SIZE,
} from 'static/Constants';
import { CERTIFICATE_ACTIONS, SUPPORT_COMMON_TYPES } from './constants';

import './AddCertificateModal.less';

const currentStepDefine = [0, 1];
const stepDefault = currentStepDefine[0];

const AddCertificateModal = (props) => {
  const { id: _id, productId: _productId } = useParams();

  const id = _id || _productId;

  const { certificateActions, setCertificateActions, isFullView } = props;

  const dispatch = useDispatch();
  const [formInstance] = Form.useForm();
  let selectedProducts = useSelector(
    gridSelector.makeSelectDetailCurrentITemsSelection()
  );

  const { productFull } = useGetProductFullView({ productId: id });

  const [currentStep, setCurrentStep] = useState(stepDefault);
  const [selectedProduct, setSelectedProduct] = useState(selectedProducts);
  const [isAllowedUpload, setIsAllowedUpload] = useState(false);
  const [productAssets, setProductAssets] = useState([]);

  const { addMultipleCertificate, getCertificatesForProducts } =
    useUpdateCertificate();

  const handleRemoveProduct = (itemData) => {
    setSelectedProduct((prev) => {
      return prev?.filter((product) => product?.id !== itemData?.id);
    });

    setProductAssets((prev) => {
      return prev?.filter((product) => product?.productItemId !== itemData?.id);
    });
  };

  const handleBackToCreateAsset = () => setCurrentStep(currentStepDefine[0]);

  const handleMoveToLinkAsset = () => setCurrentStep(currentStepDefine[1]);

  const onSelectAttachFile = (fileList) => {
    if (!fileList) return;
    const file = fileList.slice(-1).pop();
    if (!file) return;

    setProductAssets([]);

    if (file?.status === 'error.fileType') {
      setIsAllowedUpload(false);
      return;
    }

    setIsAllowedUpload(true);
  };

  const handleUploadSuccess = (res) => {
    handleMoveToLinkAsset();
    if (!res?.data) return;

    const { data } = res?.data;

    if (productAssets?.length === 0) {
      const assets = data?.productAssets?.map((item) => {
        return {
          digitalAssetId: item?.assetId,
          productItemId: item?.productId,
        };
      });
      setProductAssets(assets);
    }
    handleMoveToLinkAsset();
    setIsAllowedUpload(false);
  };

  const handleAddCertificate = async () => {
    try {
      const formValues = await formInstance.validateFields();

      const params = {
        ...formValues,
        linkType: 'CERTIFICATE',
        certificateExpiryDate: exportFormDateWithoutTime(
          formValues?.certificateExpiryDate
        ),
        documents: productAssets,
      };

      await addMultipleCertificate.mutateAsync(params, {
        onSuccess: ({ isSuccess, message, data }) => {
          if (isSuccess) {
            CustomNotification.success('Certificate added successfully');
            setCertificateActions(null);
            if (!isFullView) {
              dispatch(actionsGridView.updateItemsSelection([]));
              dispatch(actionsGridView.updateDetailCurrentItemsSelection([]));
            }
          } else {
            if (data?.lockedProducts?.length > 0) {
              showLockedProducts(data?.lockedProducts);
            } else
              CustomNotification.error(message || 'Failed to add certificate.');
          }
        },
      });
    } catch (error) {
      console.error(error);
      CustomNotification.error('Server Error!');
    }
  };

  const getProductCertificates = async () => {
    try {
      const { certificateType } = await formInstance.validateFields();
      const productIds = selectedProduct?.map((item) => item?.id) || [];

      const params = {
        productIds: [...(isFullView ? [productFull?.productId] : productIds)],
      };

      await getCertificatesForProducts.mutateAsync(params, {
        onSuccess: ({ isSuccess, data }) => {
          if (isSuccess) {
            handleValidate({
              productCertificates: data?.productCertificates,
              certificateType: certificateType,
            });
          } else {
            CustomNotification.error('Server Error!!');
          }
        },
      });
    } catch (error) {
      console.log('error: ', error);
    }
  };

  const { handleValidate, showLockedProducts } = useValidateCertificate({
    onSubmit: handleAddCertificate,
  });

  const productIds = selectedProduct?.map((product) => product?.id) || [];

  const stepDefine = [
    {
      step: 0,
      component: (
        <div style={{ height: 350, padding: '0 20px' }}>
          <UploadFile
            showList
            showMultiple={false}
            showSubmitBtn
            onFileChange={onSelectAttachFile}
            supportTypes={SUPPORT_COMMON_TYPES}
            manualUpload
            showPreview
            apiUrl={UPLOAD_DIGITAL_ASSET_FOR_PRODUCT}
            additionalBodyPayload={{
              assetType: ASSET_TYPE[2],
              assetSubType: DOCUMENT_SUBTYPE[0],
              productIds: isFullView ? [id] : productIds,
            }}
            getResponse={handleUploadSuccess}
            maxSize={UPLOAD_MAX_SIZE.GENERAL}
            showServerMessage
            forceDisableManudalUpload={!isAllowedUpload}
          />
        </div>
      ),
    },
    {
      step: 1,
      component: <CertificateForm form={formInstance} />,
    },
  ];

  return (
    <StyledModal
      title='Add Certificate'
      width={isFullView ? 650 : 1150}
      destroyOnClose
      maskClosable={false}
      visible={certificateActions === CERTIFICATE_ACTIONS.ADD}
      onCancel={() => setCertificateActions(null)}
      bodyStyle={{ height: 'min(600px, 60vh)' }}
      footer={
        <CertificateFooter
          hasDigitalAssets={productAssets?.length > 0}
          onSubmit={getProductCertificates}
          currentStep={currentStep}
          onCancel={setCertificateActions}
          isAllowedUpload={isAllowedUpload}
          loading={
            addMultipleCertificate.isLoading ||
            getCertificatesForProducts.isLoading
          }
          handleBackToCreateAsset={handleBackToCreateAsset}
          handleMoveToLinkAsset={handleMoveToLinkAsset}
        />
      }
    >
      <Row style={{ height: '100%' }}>
        {!isFullView && (
          <Col span={11}>
            <Typography.Paragraph strong>
              Selecting Products:
            </Typography.Paragraph>
            <div className='scroller' style={{ height: 514 }}>
              {selectedProduct?.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                  {selectedProduct.map((item) => (
                    <did className='list-product-form__thumb'>
                      {selectedProduct?.length > 1 ? (
                        <div
                          className='list-product-form__thumb-remove'
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveProduct(item);
                          }}
                        >
                          <CloseCircleFilled />
                        </div>
                      ) : null}
                      <div style={{ pointerEvents: 'none' }}>
                        <ThumbnailItem
                          key={item.id}
                          imageThumbnail={item?.thumb || item?.thumbnail}
                          dataDetail={item}
                          isProductThumbnail
                          isShareBtn
                        />
                      </div>
                    </did>
                  ))}
                </div>
              ) : (
                <div className='search-product-form__empty'>
                  <Empty description='No Product Selected' />
                </div>
              )}
            </div>
          </Col>
        )}
        <Divider type='vertical' style={{ height: '100%' }} />
        <Col span={isFullView ? 23 : 12}>
          <Row style={{ marginBottom: 40 }}>
            <Col xs={24}>
              <Steps current={currentStep}>
                <Steps.Step title='Create an asset' />
                <Steps.Step title={'Link to QA Specification'} />
              </Steps>
            </Col>
          </Row>
          <Row>
            <Col xs={24}>
              <StepForm currentStep={currentStep} stepDefine={stepDefine} />
            </Col>
          </Row>
        </Col>
      </Row>
    </StyledModal>
  );
};

export default AddCertificateModal;
